import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Wallet as WalletIcon, ArrowRight } from "lucide-react";

export default function Wallet() {
  const { user, refreshUser } = useAuth();
  const [balance, setBalance] = useState(null);
  const [txs, setTxs] = useState([]);
  const [wallet, setWallet] = useState(user?.wallet_address || "");
  const [amount, setAmount] = useState(1000);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [b, t] = await Promise.all([
      api.get("/credits/balance"),
      api.get("/credits/transactions"),
    ]);
    setBalance(b.data);
    setTxs(t.data.transactions || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setWallet(user?.wallet_address || "");
  }, [user?.wallet_address]);

  const connect = async () => {
    if (!wallet || wallet.length < 10) {
      toast.error("Enter a valid wallet address");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/connect-wallet", { wallet_address: wallet });
      toast.success("Wallet connected");
      await refreshUser();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/credits/convert", { amount_bc: amount });
      toast.success(`Received ${data.tokens} UNI2 (tx ${data.tx_hash.slice(0, 10)}…)`);
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="px-4 py-5 flex flex-col gap-4" data-testid="wallet-page">
        <h1 className="font-pixel text-[14px] text-glow-green">◆ WALLET</h1>

        <section className="cb-card p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[#8b95a9] font-pixel">
              BC BALANCE
            </span>
            <span
              className="font-pixel text-[16px] text-[#f7931a] text-glow-gold"
              data-testid="wallet-bc-balance"
            >
              {balance?.boom_credits ?? 0}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[#8b95a9] font-pixel">
              PENDING UNI2
            </span>
            <span className="font-mono text-[13px] text-[#2ecc71]">
              {balance?.pending_tokens ?? 0}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[#8b95a9] font-pixel">RATE</span>
            <span className="font-mono text-[11px] text-white">
              {balance?.conversion_rate} BC = 1 UNI2
            </span>
          </div>
        </section>

        <section className="cb-card p-4 flex flex-col gap-3">
          <h2 className="font-pixel text-[10px] text-[#2e75b6]">
            <WalletIcon size={12} className="inline mr-2" />
            UNI2 WALLET
          </h2>
          <input
            className="cb-input"
            placeholder="0x..."
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            data-testid="wallet-address-input"
          />
          <button
            onClick={connect}
            disabled={busy}
            data-testid="wallet-connect-btn"
            className="cb-neon-btn blue"
          >
            {user?.wallet_address ? "Update Wallet" : "Connect Wallet"}
          </button>
        </section>

        <section className="cb-card p-4 flex flex-col gap-3">
          <h2 className="font-pixel text-[10px] text-[#ff6b00]">
            BC → UNI2 CONVERT
          </h2>
          <label className="text-[10px] text-[#8b95a9]">Amount (BC)</label>
          <input
            type="number"
            min={100}
            step={100}
            className="cb-input"
            value={amount}
            onChange={(e) => setAmount(parseInt(e.target.value || 0, 10))}
            data-testid="wallet-convert-amount"
          />
          <div className="text-[11px] text-[#8b95a9]">
            You receive ≈{" "}
            <span className="text-[#2ecc71]">
              {balance ? (amount / balance.conversion_rate).toFixed(4) : 0} UNI2
            </span>
          </div>
          <button
            onClick={convert}
            disabled={busy || !user?.wallet_address}
            data-testid="wallet-convert-btn"
            className="cb-neon-btn orange"
          >
            Convert <ArrowRight size={12} className="inline ml-1" />
          </button>
          {!user?.wallet_address && (
            <div className="text-[10px] text-[#ff6b00]">
              Connect a wallet first.
            </div>
          )}
        </section>

        <section>
          <h3 className="font-pixel text-[10px] text-[#2ecc71] mb-2">
            ▼ TRANSACTIONS
          </h3>
          <div className="flex flex-col gap-2">
            {txs.length === 0 && (
              <p className="text-[11px] text-[#8b95a9]">No transactions yet.</p>
            )}
            {txs.slice(0, 20).map((t) => (
              <div
                key={t.id}
                className="cb-card p-3 flex justify-between items-center"
                data-testid={`tx-${t.id}`}
              >
                <div className="min-w-0">
                  <div className="text-[11px] truncate max-w-[180px]">
                    {t.description}
                  </div>
                  <div className="text-[9px] text-[#8b95a9]">{t.type}</div>
                </div>
                <div
                  className={`font-mono text-[12px] ${
                    t.amount >= 0 ? "text-[#2ecc71]" : "text-[#ff6b00]"
                  }`}
                >
                  {t.amount >= 0 ? "+" : ""}
                  {t.amount}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
